import {
    Chip,
    ClickAwayListener,
    FormControl,
    FormControlLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import {memo, useCallback, useMemo, useRef, useState} from 'react';
import { useStyles } from "./FilterDropdownStyles.js";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from '@mui/icons-material/DoneAll';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ReactComponent as FilterIcon } from '../../Icons/filter.svg';
// import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PrimeButton from "../PrimeButton";
import EntryListMenuButton from '../Menu/EntryListMenuButton.js';
import EscapeListener from '../../Components/Utils/EscapeListener';
import { getCategoriesFromEntries, getTagsFromEntries } from '../../Utils/Entry';
import TabAwayListener from '../Utils/TabAwayListener.js';
import BasicButton from '../Buttons/BasicButton.js';

const CheckIconComponent = <CheckIcon />

const FilterDropdownMenuMemo = memo(function FilterDropdownMenu({ chipsSelected = [], items, onItemClick, btnText }) {
    const classes = useStyles();

    const handleChipClick = useCallback(
        (e) => onItemClick(e.currentTarget.dataset.itemId), 
        [onItemClick]
    );

    return (
        <>
            <Paper className={classes.filterDropdownMenu} alignitems="center" elevation={0}>
                <Typography component="h4">{btnText}</Typography>
                <Typography component="h6">{chipsSelected.length} Selected</Typography>
            </Paper>
            <Paper component="ul" className={classes.filterDropdownList} >
                {items?.map((item, i) => (
                    <li className={classes.listItem} key={item.id} >
                        <Chip
                            label={item.title}
                            onClick={handleChipClick}
                            className={`${classes.chipItem} ${(chipsSelected.includes(item.id)) ? classes.chipSelected : '' }`}
                            // aria-selected={true}
                            data-item-id={item.id}
                        />
                    </li>
                ))}
            </Paper>
        </>
    );
});


export const EMPTY_FILTERS = {
    categoriesSelected: [],
    tagsSelected: [],
    watched: `all`,
    notes: `all`
};

export default memo(function FilterDropdown({ award, entries, activeFilters, onFilterChange, displayCategories = true, additionalTags = null, }) {
    
    const classes = useStyles();
    const buttonRef = useRef();

    const [dropdownOpen, setDropdownOpen] = useState(false);

    const filterDropDownRef = useRef(null);

    const initialFilters = { ...EMPTY_FILTERS, ...activeFilters };
    delete initialFilters.searchQuery;
    const [localFilters, setLocalFilters] = useState(initialFilters);
    const [totalSelectedCount, setTotalSelectedCount] = useState(0);

    const categories = useMemo(() => getCategoriesFromEntries(entries), [entries]);
    const tags = useMemo(() => [...(additionalTags ?? []), ...(getTagsFromEntries(entries) ?? [])], [additionalTags, entries]);
    console.log("filter tags", tags);

    const handleClick = useCallback((e) => {
        console.log("e.target", e.target);
        console.log("!dropdownOpen", !dropdownOpen);
        setDropdownOpen(!dropdownOpen);
    }, [setDropdownOpen, dropdownOpen]);

    const selectedByTagToggle = useCallback((tagId) => {
        setLocalFilters({
            ...localFilters,
            tagsSelected: localFilters.tagsSelected.includes(tagId) 
                ? localFilters.tagsSelected.filter((key) => key !== tagId) 
                : [...localFilters.tagsSelected, tagId]
        });
    }, [localFilters]);

    const selectedByCatToggle = useCallback((catIdString) => {
        const catId = parseInt(catIdString);
        setLocalFilters({
            ...localFilters,
            categoriesSelected: localFilters.categoriesSelected.includes(catId) 
                ? localFilters.categoriesSelected.filter((key) => key !== catId) 
                : [...localFilters.categoriesSelected, catId]
        }); 
    }, [setLocalFilters, localFilters]);

    const clearAllFilters = useCallback(() =>  {
        onFilterChange?.(EMPTY_FILTERS);

        setLocalFilters(EMPTY_FILTERS);
        setTotalSelectedCount(0);
        
        setDropdownOpen(false);
    }, [setLocalFilters, setTotalSelectedCount, setDropdownOpen, onFilterChange]);

    const selectAllFilters = useCallback(() => {
        const tagsSelected = tags ? Object.keys(tags).map((key) =>  tags[key].id) : []
        const catsSelected = categories ? Object.keys(categories).map((key) =>  categories[key].id) : []
        setLocalFilters({
            categoriesSelected: catsSelected,
            tagsSelected: tagsSelected,
            watched: `all`,
        })
    }, [tags, categories, setLocalFilters]);

    const applySelectedFilters = useCallback(() => {
        onFilterChange?.(localFilters);
        setDropdownOpen(false);
    }, [localFilters, onFilterChange]);

    const handleEscapePressed = useCallback(() => {
        setDropdownOpen(false)
        if(buttonRef.current) 
            buttonRef.current.focus();
    }, [setDropdownOpen, buttonRef]);

    const handleTabAway = useCallback(() => {
        setDropdownOpen(false)
    }, [setDropdownOpen]);

    const handleWatchedChanged = useCallback(e => {
        setLocalFilters({ ...localFilters, watched: e.target.value})
    }, [setLocalFilters, localFilters]);

    const handleNotesChanged = useCallback(e => {
        setLocalFilters({ ...localFilters, notes: e.target.value })
    }, [setLocalFilters, localFilters])

    const RadioNotesAll = useMemo(() => (
        <Radio checked={localFilters.notes === `all`} />
    ), [localFilters.notes]);
    const RadioNotesSaved = useMemo(() => (
        <Radio checked={localFilters.notes === `saved`} />
    ), [localFilters.notes]);
    const RadioNotesEmpty = useMemo(() => (
        <Radio checked={localFilters.notes === `empty`} />
    ), [localFilters.notes]);

    const RadioWatchedAll = useMemo(() => (
        <Radio checked={localFilters.watched === `all`} />
    ), [localFilters.watched]);
    const RadioWatchedWatched = useMemo(() => (
        <Radio checked={localFilters.watched === `watched`} />
    ), [localFilters.watched]);
    const RadioWatchedUnwatched = useMemo(() => (
        <Radio checked={localFilters.watched === `unwatched`} />
    ), [localFilters.watched]);
    
    return (
        <>
        <div className={`${classes.root}`}>
            <EntryListMenuButton
                onClick={handleClick}
                anchorRef={buttonRef} 
                icon={FilterIcon}
                open={dropdownOpen}
            >
                Filter
                {(totalSelectedCount > 0 && dropdownOpen) ? (
                    <Typography component="h6" variant="button"  className={classes.filtersCount}>{totalSelectedCount}</Typography>
                ):''}
            </EntryListMenuButton>
            
            <EscapeListener enabled={dropdownOpen} onKeyPress={handleEscapePressed}>
                
                <TabAwayListener tabRef={filterDropDownRef} enabled={dropdownOpen} tabAwayHandler={handleTabAway}>
                    <div
                        className={`${classes.menu} ${dropdownOpen ? classes.menuOpen : ''}`}
                        ref={filterDropDownRef}
                    >
                        {dropdownOpen ? (
                            <ClickAwayListener onClickAway={handleClick}>
                                <div className={classes.menuList}>
                                    <Grid container spacing={3}>
                                        {tags?.length ? (
                                            <Grid item md={12} sm={12} xs={12} lg={12}>
                                                    <FilterDropdownMenuMemo
                                                        btnText="FILTER BY TAG"
                                                        items={tags}
                                                        onItemClick={selectedByTagToggle}
                                                        chipsSelected={localFilters.tagsSelected}
                                                    />
                                            </Grid>
                                        ) : null}
                                        {displayCategories && categories?.length && (
                                            <Grid item md={12} sm={12} lg={12}>
                                                    <FilterDropdownMenuMemo
                                                        btnText="FILTER BY CATEGORY"
                                                        items={categories}
                                                        onItemClick={selectedByCatToggle}
                                                        chipsSelected={localFilters.categoriesSelected}
                                                    />
                                            </Grid>
                                        )}
                                        <Grid item md={12} sm={12} lg={12}>
                                            
                                            <FormControl>
                                                <RadioGroup
                                                    aria-labelledby="Watched"
                                                    defaultValue="all"
                                                    name="watched" 
                                                    row
                                                    className={classes.watchedFilter}
                                                    onChange={handleWatchedChanged}
                                                >
                                                    <FormControlLabel 
                                                        value="all"
                                                        label="All" 
                                                        control={RadioWatchedAll} 
                                                    />
                                                    <FormControlLabel 
                                                        value="watched" 
                                                        label={award.judgingMediaType === 'video' ? "Watched only" : "Played only"}
                                                        control={RadioWatchedWatched} 
                                                    />
                                                    <FormControlLabel 
                                                        value="unwatched"
                                                        label={award.judgingMediaType === 'video' ? "Unwatched only" : "Yet to play only"} 
                                                        control={RadioWatchedUnwatched}  
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                
                                        </Grid>
                                        <Grid item md={12} sm={12} lg={12}>
                                            
                                            <FormControl>
                                                <RadioGroup
                                                    aria-labelledby="All"
                                                    defaultValue="all"
                                                    name="notes" 
                                                    row
                                                    className={classes.watchedFilter}
                                                    onChange={handleNotesChanged}
                                                >
                                                    <FormControlLabel 
                                                        value="all"
                                                        label="All" 
                                                        control={RadioNotesAll} 
                                                    />
                                                    <FormControlLabel 
                                                        value="saved" 
                                                        label="Saved notes"
                                                        control={RadioNotesSaved} 
                                                    />
                                                    <FormControlLabel 
                                                        value="empty" 
                                                        label="Empty notes"
                                                        control={RadioNotesEmpty}  
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={3} className={`${classes.actionsContainer}`}>
                                        <PrimeButton className={`${classes.submitButton}`}
                                            icon={CheckIconComponent} text="APPLY FILTERS"
                                            onClick={applySelectedFilters} 
                                        /> 
                                        <BasicButton className={`${classes.clearAllButton}`}
                                            startIcon={CloseIcon} onClick={clearAllFilters}
                                        >
                                            Clear all
                                        </BasicButton>
                                        <BasicButton className={`${classes.selectAllButton}`}
                                            startIcon={DoneAllIcon}
                                            onClick={selectAllFilters}
                                        >
                                            SELECT ALL
                                        </BasicButton>
                                    </Grid>
                                </div>
                            </ClickAwayListener>
                        ):''}
                    </div>
                </TabAwayListener>
            </EscapeListener>
        </div>
        </>
    );
});